import type {
  SiteComplianceNetworkTier,
  SiteComplianceSite,
  SiteComplianceSiteType,
} from '../../siteCompliance.types';
import type {
  SiteComplianceInventoryPropertyValue,
  SiteComplianceInventoryResource,
  SiteComplianceInventoryVendor,
} from '../../siteComplianceInventory.types';
import type { SiteDetailFacts } from '../types';
import type {
  InventoryResourceDto,
  InventorySiteDetailDto,
  InventorySiteDto,
  NetboxDatasetResourceDto,
} from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) {
    return fallback;
  }

  return `${value}`;
};

const asNullableString = (value: unknown) => {
  const normalized = asString(value).trim();
  return normalized ? normalized : null;
};

const splitMultiValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map((entry) => asString(entry).trim()).filter(Boolean);
  }

  return asString(value)
    .split(/[;,/|]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const normalizeSiteType = (value: unknown): SiteComplianceSiteType => {
  const normalized = asString(value).trim().toLowerCase().replace(/[\s-]+/g, '_');

  if (
    normalized === 'office' ||
    normalized === 'office_plus' ||
    normalized === 'flex' ||
    normalized === 'datacenter'
  ) {
    return normalized;
  }

  return 'office';
};

const normalizeNetworkTier = (value: unknown): SiteComplianceNetworkTier => {
  const normalized = asString(value).trim().toLowerCase();

  if (
    normalized === 'standard' ||
    normalized === 'enhanced' ||
    normalized === 'flagship'
  ) {
    return normalized;
  }

  return null;
};

const getCustomField = (
  dto: InventorySiteDto | InventorySiteDetailDto,
  key: string,
) => {
  const customFields =
    'details' in dto && isRecord(dto.details?.custom_fields)
      ? dto.details.custom_fields
      : null;

  return customFields?.[key];
};

const getSiteName = (dto: InventorySiteDto) =>
  asString(dto.name ?? dto.id, 'unknown-site');

const toPropertyValue = (value: unknown): SiteComplianceInventoryPropertyValue => {
  if (
    value === null ||
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'string'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => asString(entry));
  }

  if (isRecord(value)) {
    return JSON.stringify(value);
  }

  return asString(value);
};

const toStringRecord = (value: unknown) => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== null && entry !== undefined)
      .map(([key, entry]) => [key, asString(entry)]),
  );
};

const toPropertiesRecord = (value: unknown) => {
  if (!isRecord(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .map(([key, entry]) => [key, toPropertyValue(entry)]),
  );
};

const normalizeVendor = (
  vendor: unknown,
  type: unknown,
  id: unknown,
): SiteComplianceInventoryVendor => {
  const normalized = [vendor, type, id]
    .map((entry) => asString(entry).trim().toLowerCase())
    .join(' ');

  if (
    normalized.includes('aws') ||
    normalized.includes('amazon') ||
    normalized.includes('arn:') ||
    normalized.includes('route53') ||
    normalized.includes('ec2-')
  ) {
    return 'aws';
  }

  if (
    normalized.includes('azure') ||
    normalized.includes('microsoft') ||
    normalized.includes('/subscriptions/')
  ) {
    return 'azure';
  }

  return 'unknown';
};

const getInventoryResourceTags = (dto: InventoryResourceDto) => {
  const wkTags = Object.fromEntries(
    Object.entries(dto).filter(
      ([key, value]) => key.startsWith('tagWk') && value !== null && value !== undefined,
    ),
  );

  return {
    ...toStringRecord(dto.tags),
    ...toStringRecord(wkTags),
  };
};

export const mapInventorySiteToUi = (
  dto: InventorySiteDto,
): SiteComplianceSite => {
  const siteId = getSiteName(dto);
  const customSiteType = getCustomField(dto, 'site_type');
  const customNetworkTier = getCustomField(dto, 'network_tier');
  const customPrimaryDivision = getCustomField(dto, 'primary_division');
  const customBusinessUnits = getCustomField(dto, 'business_units');
  const siteType = normalizeSiteType(dto.siteType ?? customSiteType);
  const networkTier = normalizeNetworkTier(dto.networkTier ?? customNetworkTier);
  const primaryDivisions = splitMultiValue(
    dto.primaryDivision ?? customPrimaryDivision,
  );
  const businessUnits = splitMultiValue(dto.businessUnits ?? customBusinessUnits);

  return {
    address:
      asNullableString(dto.physicalAddress) ??
      asNullableString(dto.shippingAddress),
    businessUnits,
    country: null,
    currentCompliance: null,
    evaluationId: null,
    isDatacenter: siteType === 'datacenter',
    isEnhanced: networkTier === 'enhanced',
    isFlagship: networkTier === 'flagship',
    lastReviewedAt: null,
    networkTier,
    primaryDivision:
      asNullableString(dto.primaryDivision ?? customPrimaryDivision) ??
      primaryDivisions[0] ??
      null,
    primaryDivisions,
    region: asString(dto.regionName ?? dto.regionId),
    siteId,
    siteName: siteId,
    siteType,
    source: 'netbox',
    workdayId: null,
  };
};

export const mapInventorySiteDetailToUi = (
  dto: InventorySiteDetailDto | null | undefined,
): SiteDetailFacts => ({
  latestConfiguration: null,
  site: dto ? mapInventorySiteToUi(dto) : null,
});

export const mapInventoryResourceToUi = (
  dto: InventoryResourceDto,
  siteId: string,
): SiteComplianceInventoryResource => {
  const id = asString(dto.id, asString(dto.name, 'inventory-resource'));
  const type = asString(dto.type, 'resource');

  return {
    id,
    linkedCheckpoints: [],
    name: asString(dto.name ?? dto.tagWkResourceName, id),
    properties: toPropertiesRecord(dto.properties),
    region: asString(dto.region),
    siteId,
    subscriptionIdOrAccountId: asString(dto.subscriptionId),
    tags: getInventoryResourceTags(dto),
    type,
    vendor: normalizeVendor(dto.vendor, type, id),
  };
};

export const mapNetboxDatasetResourceToUi = (
  dto: NetboxDatasetResourceDto,
  siteId: string,
  dataset: string,
): SiteComplianceInventoryResource => {
  const details = isRecord(dto.details) ? dto.details : {};
  const id = asString(
    dto.id ?? details.id ?? dto.name ?? dto.display ?? dto.prefix ?? dto.address,
    `${dataset}:${siteId}`,
  );
  const name = asString(
    dto.name ??
      dto.display ??
      dto.prefix ??
      dto.address ??
      dto.cid ??
      details.display ??
      details.name,
    id,
  );
  const type = asString(dto.type ?? details.type ?? dataset, dataset);

  return {
    id: `${dataset}:${id}`,
    linkedCheckpoints: [],
    name,
    properties: {
      sourceDataset: dataset,
      ...toPropertiesRecord(dto),
    },
    region: '',
    siteId,
    subscriptionIdOrAccountId: '',
    tags: {
      sourceDataset: dataset,
    },
    type,
    vendor: 'unknown',
  };
};
